// src/components/subscriptionPeriod.ts
/**
 * Turning a Stripe period end into a date a member can read.
 *
 * This is a plain function over a number, in its own module, for the reason set out at the top of
 * `api/errors.ts`: CRA 5 runs the tests on jest 27, and anything that reaches axios or the Redux
 * store can no longer be exercised as a function. `SubscriptionDetails.tsx` pulls in both, so the
 * one piece of it that can be wrong without anybody noticing by looking lives here instead.
 *
 * WHAT CAN GO WRONG, AND WHY THE GUARD IS NOT DEFENSIVE PADDING.
 *
 * `SubscriptionDto.currentPeriodEnd` is a Java primitive `long`, so it is not nullable and it is not
 * absent — a member who bought a one-off pass and has no Stripe subscription at all gets `0`. Fed
 * straight to `new Date(0 * 1000)` that renders "1. 1. 1970", and the cancel-at-period-end sentence
 * would then read "prístup máte do 1. 1. 1970" to somebody who has just paid. The answer for a
 * value that says nothing is to say nothing, which is what `null` means here.
 *
 * The date is assembled by hand rather than by `toLocaleDateString('sk-SK')`. The two agree — sk-SK
 * formats as `d. M. y` — but the manual form has no opinion about which ICU data the browser or the
 * test runner shipped with, so it cannot quietly become "9/10/2026" on a build that trimmed its
 * locales.
 */

/**
 * `1760486400` → `"15. 10. 2026"`, in the reader's own time zone; `null` for anything that is not a
 * real point in time (absent, `0`, negative, NaN, or large enough to overflow `Date`).
 *
 * The argument is in SECONDS, which is what Stripe sends and what every field on `SubscriptionDto`
 * carries — `currentPeriodEnd` and `premiumEndDate` alike.
 */
export function formatPeriodEnd(epochSeconds: number | null | undefined): string | null {
    if (typeof epochSeconds !== 'number' || !isFinite(epochSeconds) || epochSeconds <= 0) {
        return null;
    }
    const date = new Date(epochSeconds * 1000);
    const time = date.getTime();
    if (isNaN(time)) {
        return null;
    }
    return date.getDate() + '. ' + (date.getMonth() + 1) + '. ' + date.getFullYear();
}
